/**
 * Deterministic Nutrition Engine — shared constants.
 *
 * Scientific foundations:
 * - Mifflin et al. (1990), "A New Predictive Equation for Resting Energy
 *   Expenditure in Healthy Individuals" — default adult BMR equation.
 * - Hall et al. (2011), "Quantification of the Effect of Energy Imbalance on
 *   Bodyweight" and Chow & Hall (2008), "The Dynamics of Human Body Weight
 *   Change" — basis for the dynamic projection approximation (declining
 *   energy expenditure with weight change + adaptive thermogenesis term).
 * - National Academies DRI guidance — macronutrient/fiber references
 *   (fiber ~14 g per 1,000 kcal).
 *
 * The 7,700 kcal/kg tissue energy density is used ONLY as a documented
 * short-term approximation for initial deficit sizing and stored-energy
 * deltas — never for long-term projections (those use the dynamic
 * simulation in simulation.ts).
 */

/** Algorithm versions per calculation kind (bump independently). */
export const ALGO_VERSIONS = {
  bmi: "1.0.0",
  bmr: "1.0.0",
  initialTdee: "1.0.0",
  calorieTarget: "1.0.0",
  macroTargets: "1.0.0",
  goalDuration: "1.0.0",
  forecast: "1.0.0",
  trendWeight: "1.0.0",
  adaptiveExpenditure: "1.0.0",
  weeklyAdjustment: "1.0.0",
} as const;

/** kcal per gram. */
export const KCAL_PER_G_PROTEIN = 4;
export const KCAL_PER_G_CARBS = 4;
export const KCAL_PER_G_FAT = 9;

/**
 * Short-term approximation of tissue energy density (kcal per kg).
 * Documented approximation only — used for initial deficit sizing and
 * stored-energy deltas in the adaptive expenditure update.
 */
export const TISSUE_KCAL_PER_KG = 7700;

/** Standard adult BMI class boundaries. */
export const BMI_UNDERWEIGHT_MAX = 18.5;
export const BMI_HEALTHY_MAX = 25;
export const BMI_OVERWEIGHT_MAX = 30;

/** Conventional BMR activity multipliers (initial fallback only). */
export const ACTIVITY_MULTIPLIERS: Record<string, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};
export const DEFAULT_ACTIVITY_MULTIPLIER = 1.2;

/**
 * Steps-based multiplier nudge: each 1,000 average daily steps above/below
 * the 5,000-step reference shifts the multiplier by this amount, clamped.
 */
export const STEPS_REFERENCE = 5000;
export const STEPS_MULTIPLIER_PER_1000 = 0.025;
export const STEPS_ADJUSTMENT_MAX = 0.15;

/**
 * Diet-plan aggression mapped to % of current body weight per week.
 * Product pace values are preserved: relaxed/steady/aggressive.
 */
export const AGGRESSION_WEEKLY_PERCENT_LOSE: Record<string, number> = {
  relaxed: 0.25,
  steady: 0.5,
  aggressive: 0.75,
};
export const AGGRESSION_WEEKLY_PERCENT_GAIN: Record<string, number> = {
  relaxed: 0.15,
  steady: 0.25,
  aggressive: 0.4,
};
/** Absolute safety ceiling for automated weight loss (% bw/week). */
export const MAX_WEEKLY_PERCENT_LOSE = 1.0;
/** Near-goal rate dampening: within this many kg of goal, cap the rate. */
export const NEAR_GOAL_KG = 2;
export const NEAR_GOAL_MAX_WEEKLY_PERCENT = 0.25;

/** Intake safety limits. */
export const ABSOLUTE_MIN_KCAL = 1200;
export const ABSOLUTE_MAX_KCAL = 4000;
/** Never programme an intake below this fraction of BMR for weight loss. */
export const MIN_KCAL_BMR_FRACTION_LOSE = 0.75;
/** Flag (but still show) targets below this fraction of BMR as unusually low. */
export const LOW_KCAL_BMR_FRACTION_FLAG = 0.85;
/** Max surplus fraction of expenditure for automated gain programmes. */
export const MAX_SURPLUS_FRACTION_GAIN = 1.25;

/** Protein targets (g per kg reference weight), evidence-based ranges. */
export const PROTEIN_PER_KG = {
  lose: 1.6,
  muscle: 1.6,
  gain: 1.6,
  maintain: 1.4,
  healthier: 1.4,
} as const;
export const PROTEIN_PER_KG_MIN = 1.2;
export const PROTEIN_PER_KG_MAX = 2.2;
/** Protein never exceeds this fraction of total calories. */
export const PROTEIN_MAX_KCAL_FRACTION = 0.35;
export const PROTEIN_MIN_G = 40;
export const PROTEIN_MAX_G = 250;

/** Minimum dietary fat before allocating remaining calories. */
export const FAT_MIN_G_PER_KG = 0.6;
export const FAT_MIN_KCAL_FRACTION = 0.2;
export const FAT_ABSOLUTE_MIN_G = 35;

/** Fiber: ~14 g per 1,000 kcal, clamped to practical daily targets. */
export const FIBER_G_PER_1000_KCAL = 14;
export const FIBER_MIN_G = 21;
export const FIBER_MAX_G = 45;

/** Dynamic projection (Hall/NIH-inspired documented approximation). */
export const SIM_MAX_WEEKS = 520;
/** Adaptive thermogenesis: kcal/day reduction per kg lost during dieting. */
export const ADAPTIVE_THERMOGENESIS_KCAL_PER_KG = 10;
/** Timeline range half-width (±15% around central estimate). */
export const TIMELINE_RANGE_FRACTION = 0.15;

/** Trend weight (robust EWMA). */
export const TREND_ALPHA = 0.35;
export const TREND_DRIFT_ALPHA = 0.1;
/** Residuals beyond this many robust sigmas are downweighted (Huber-style). */
export const TREND_OUTLIER_SIGMAS = 2.5;
export const TREND_MIN_SIGMA_KG = 0.15;
export const TREND_INITIAL_SIGMA_KG = 0.5;

/** Adaptive expenditure update. */
export const EXPENDITURE_ALPHA_MIN = 0.05;
export const EXPENDITURE_ALPHA_MAX = 0.4;
/** Per-update change clamp (fraction of previous estimate). */
export const EXPENDITURE_MAX_CHANGE_FRACTION = 0.05;
/** Plausibility bounds relative to BMR. */
export const EXPENDITURE_MIN_BMR_FRACTION = 0.6;
export const EXPENDITURE_MAX_BMR_FRACTION = 3.0;
/** Data sufficiency thresholds. */
export const SUFFICIENCY_WINDOW_DAYS = 7;
export const SUFFICIENCY_MIN_COMPLETE_DAYS = 4;
export const SUFFICIENCY_WEIGHIN_WINDOW_DAYS = 14;
export const SUFFICIENCY_MIN_WEIGHINS = 3;

/** Weekly programme adjustment. */
export const WEEKLY_RATE_TOLERANCE_PERCENT = 0.1;
export const WEEKLY_ADJUSTMENT_MIN_KCAL = 25;
export const WEEKLY_ADJUSTMENT_MAX_KCAL = 150;

export const MS_PER_DAY = 86_400_000;
export const MS_PER_WEEK = 7 * MS_PER_DAY;
